// src/components/Badge.js — name tags: label sizes, the default design, drawing and printing
// [marker: back-tags v1] [marker: tag-art v1] your own artwork as the background (design.bgImage), optionally with nothing else (design.plain)
// One design (JSON) is drawn three ways: the designer preview, the kiosk preview and the print-out.
// Positions are % of the tag, font sizes are points, so a design fits any label size.
// Printing uses the device's normal print (AirPrint on an iPad): one tag per label, or a full
// Avery sheet for batches.
import React from 'react';

export const PRESETS = {
  badge_4x3:      { label: '4" × 3" badge / label', w: 4, h: 3 },
  badge_3x4:      { label: '3" × 4" badge (tall)', w: 3, h: 4 },
  brother_dk1202: { label: 'Brother DK-1202 label (3.9" × 2.4")', w: 3.9, h: 2.4 },
  brother_62mm:   { label: 'Brother 62 mm roll, cut at 3.5" (3.5" × 2.4")', w: 3.5, h: 2.44 },
  dymo_30857:     { label: 'DYMO 30857 badge label (4" × 2¼")', w: 4, h: 2.25 },
  avery_5395:     { label: 'Avery 5395 sheet — 8 per page (3⅜" × 2⅓")', w: 3.375, h: 2.333, sheet: { cols: 2, rows: 4, top: 0.594, left: 0.6875, pitchX: 3.6875, pitchY: 2.5 } },
  avery_5392:     { label: 'Avery 5392 / 74459 sheet — 6 per page (4" × 3")', w: 4, h: 3, sheet: { cols: 2, rows: 3, top: 1, left: 0.25, pitchX: 4, pitchY: 3 } },
  custom:         { label: 'Custom size…', w: 4, h: 3 },
};

export const FIELDS = {
  first_name: 'First name', last_name: 'Last name', full_name: 'Full name', company: 'Company',
  title: 'Job title', ticket_type: 'Ticket type', event_name: 'Event name', event_date: 'Event date', text: 'Your own text',
};
export const FONTS = {
  sans: 'Helvetica, Arial, sans-serif', serif: 'Georgia, "Times New Roman", serif',
  rounded: '"Trebuchet MS", "Segoe UI", Arial, sans-serif', condensed: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif',
};
export const SAMPLE = { first_name: 'Rebecca', last_name: 'Goldberg-Stein', company: 'Garden State Holdings LLC', title: 'Managing Director', ticket_type: 'Member Ticket' };

let _n = 0;
export const newId = () => `f${Date.now().toString(36)}${(_n++).toString(36)}`;

export function defaultDesign(orgLogoUrl) {
  return {
    preset: 'badge_4x3', size: { w: 4, h: 3 },
    background: '#ffffff', border: false,
    bands: [{ id: 'b1', y: 0, h: 22, color: '#1a3a5c' }, { id: 'b2', y: 93, h: 7, color: '#c8a84b' }],
    logo: orgLogoUrl ? { src: orgLogoUrl, x: 3, y: 2.5, w: 17 } : null,
    fields: [
      { id: 'ev', type: 'event_name', x: orgLogoUrl ? 20 : 3, y: 6, w: orgLogoUrl ? 77 : 94, size: 11, bold: true, color: '#ffffff', align: 'center', upper: true, font: 'sans' },
      { id: 'fn', type: 'first_name', x: 4, y: 31, w: 92, size: 36, bold: true, color: '#1a3a5c', align: 'center', font: 'sans' },
      { id: 'ln', type: 'last_name', x: 4, y: 54, w: 92, size: 20, bold: false, color: '#1a3a5c', align: 'center', font: 'sans' },
      { id: 'co', type: 'company', x: 4, y: 72, w: 92, size: 13, italic: true, color: '#4b5563', align: 'center', font: 'sans' },
    ],
  };
}

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtDay = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');

export function fieldText(f, person, event) {
  const p = person || {};
  let t = '';
  switch (f.type) {
    case 'first_name': t = p.first_name; break;
    case 'last_name': t = p.last_name; break;
    case 'full_name': t = [p.first_name, p.last_name].filter(Boolean).join(' '); break;
    case 'company': t = p.company; break;
    case 'title': t = p.title; break;
    case 'ticket_type': t = p.ticket_type; break;
    case 'event_name': t = event?.name; break;
    case 'event_date': t = fmtDay(event?.event_date); break;
    default: t = f.text;
  }
  t = String(t || '');
  return f.upper ? t.toUpperCase() : t;
}

// shrink long text so it stays on one line inside its box
function fittedPt(f, text, widthIn) {
  if (!text) return f.size;
  const charEm = (f.bold ? 0.6 : 0.55) * (f.upper ? 1.12 : 1) * (f.font === 'condensed' ? 0.82 : 1);
  const needIn = (text.length * charEm * f.size) / 72;
  return needIn > widthIn ? Math.max(6, f.size * (widthIn / needIn)) : f.size;
}

export function sizeOf(design) {
  const p = PRESETS[design.preset];
  return design.preset === 'custom' || !p ? design.size : { w: p.w, h: p.h };
}

// The tag as plain HTML (inches/points) — used for printing
export function badgeHtml(design, person, event) {
  const { w, h } = sizeOf(design);
  let html = `<div class="njb-badge" style="position:relative;overflow:hidden;width:${w}in;height:${h}in;background:${esc(design.background || '#fff')};${design.border ? 'box-sizing:border-box;border:1px solid #9ca3af;' : ''}">`;
  if (design.bgImage && design.bgImage.src) {
    html += `<img src="${esc(design.bgImage.src)}" style="position:absolute;left:0;top:0;width:100%;height:100%;object-fit:${design.bgImage.fit === 'contain' ? 'contain' : 'cover'}" />`;
  }
  (design.plain ? [] : (design.bands || [])).forEach(b => {
    html += `<div style="position:absolute;left:0;right:0;top:${b.y}%;height:${b.h}%;background:${esc(b.color)}"></div>`;
  });
  if (!design.plain && design.logo && design.logo.src) {
    html += `<img src="${esc(design.logo.src)}" style="position:absolute;left:${design.logo.x}%;top:${design.logo.y}%;width:${design.logo.w}%;height:auto" />`;
  }
  (design.fields || []).forEach(f => {
    const text = fieldText(f, person, event);
    if (!text) return;
    const widthIn = (w * f.w) / 100;
    const pt = fittedPt(f, text, widthIn);
    html += `<div style="position:absolute;left:${f.x}%;top:${f.y}%;width:${f.w}%;white-space:nowrap;overflow:hidden;line-height:1.15;`
      + `font-family:${FONTS[f.font] || FONTS.sans};font-size:${pt.toFixed(2)}pt;font-weight:${f.bold ? 700 : 400};font-style:${f.italic ? 'italic' : 'normal'};`
      + `color:${esc(f.color || '#111')};text-align:${f.align || 'center'};${f.upper ? 'letter-spacing:0.04em;' : ''}">${esc(text)}</div>`;
  });
  return html + '</div>';
}

// The tag on screen — same layout, scaled
export function BadgeView({ design, person, event, scale = 1, selectedId, onPointerDownItem, style }) {
  const { w, h } = sizeOf(design);
  const ppi = 96 * scale;
  return (
    <div style={{ position: 'relative', overflow: 'hidden', width: w * ppi, height: h * ppi, background: design.background || '#fff',
      boxShadow: '0 2px 10px rgba(0,0,0,0.15)', border: design.border ? '1px solid #9ca3af' : '1px solid #e5e7eb', borderRadius: 4, touchAction: 'none', ...style }}>
      {design.bgImage && design.bgImage.src && (
        <img alt="" src={design.bgImage.src} draggable={false}
             style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', objectFit: design.bgImage.fit === 'contain' ? 'contain' : 'cover', pointerEvents: 'none' }} />
      )}
      {(design.plain ? [] : (design.bands || [])).map(b => <div key={b.id} style={{ position: 'absolute', left: 0, right: 0, top: `${b.y}%`, height: `${b.h}%`, background: b.color }} />)}
      {!design.plain && design.logo && design.logo.src && (
        <img alt="" src={design.logo.src} draggable={false}
             onPointerDown={onPointerDownItem ? (e) => onPointerDownItem(e, 'logo') : undefined}
             style={{ position: 'absolute', left: `${design.logo.x}%`, top: `${design.logo.y}%`, width: `${design.logo.w}%`, height: 'auto',
               cursor: onPointerDownItem ? 'move' : 'default', outline: selectedId === 'logo' ? '2px dashed #2563eb' : 'none' }} />
      )}
      {(design.fields || []).map(f => {
        const text = fieldText(f, person, event);
        const pt = fittedPt(f, text, (w * f.w) / 100);
        return (
          <div key={f.id} onPointerDown={onPointerDownItem ? (e) => onPointerDownItem(e, f.id) : undefined}
               style={{ position: 'absolute', left: `${f.x}%`, top: `${f.y}%`, width: `${f.w}%`, whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: 1.15,
                 fontFamily: FONTS[f.font] || FONTS.sans, fontSize: (pt * 96 * scale) / 72, fontWeight: f.bold ? 700 : 400, fontStyle: f.italic ? 'italic' : 'normal',
                 color: f.color || '#111', textAlign: f.align || 'center', letterSpacing: f.upper ? '0.04em' : undefined,
                 cursor: onPointerDownItem ? 'move' : 'default', userSelect: 'none', minHeight: (f.size * 96 * scale) / 72 * 1.1,
                 outline: selectedId === f.id ? '2px dashed #2563eb' : onPointerDownItem ? '1px dashed rgba(37,99,235,0.25)' : 'none' }}>
            {text || (onPointerDownItem ? <span style={{ opacity: 0.35 }}>{FIELDS[f.type]}</span> : '')}
          </div>
        );
      })}
    </div>
  );
}

// Print tags with the device's print dialog (AirPrint on iPad). Resolves after the dialog closes.
export function printBadges(design, people, event) {
  const preset = PRESETS[design.preset] || {};
  const { w, h } = sizeOf(design);
  const sheet = preset.sheet;
  let pages = '';
  if (sheet) {
    const per = sheet.cols * sheet.rows;
    for (let i = 0; i < people.length; i += per) {
      let page = '<div class="njb-page" style="position:relative;width:8.5in;height:11in;overflow:hidden;page-break-after:always;break-after:page">';
      people.slice(i, i + per).forEach((p, k) => {
        const col = k % sheet.cols; const row = Math.floor(k / sheet.cols);
        page += `<div style="position:absolute;left:${sheet.left + col * sheet.pitchX}in;top:${sheet.top + row * sheet.pitchY}in">${badgeHtml(design, p, event)}</div>`;
      });
      pages += page + '</div>';
    }
  } else {
    people.forEach(p => { pages += `<div class="njb-page" style="width:${w}in;height:${h}in;overflow:hidden;page-break-after:always;break-after:page">${badgeHtml(design, p, event)}</div>`; });
  }
  const pageSize = sheet ? '8.5in 11in' : `${w}in ${h}in`;
  return new Promise((resolve) => {
    const old = document.getElementById('njb-print-root'); if (old) old.remove();
    const oldStyle = document.getElementById('njb-print-style'); if (oldStyle) oldStyle.remove();
    const root = document.createElement('div'); root.id = 'njb-print-root'; root.innerHTML = pages;
    const st = document.createElement('style'); st.id = 'njb-print-style';
    st.textContent = `#njb-print-root{display:none}
@media print{@page{size:${pageSize};margin:0}html,body{margin:0!important;padding:0!important;background:#fff!important}
body>*:not(#njb-print-root){display:none!important}#njb-print-root{display:block!important}
#njb-print-root .njb-page:last-child{page-break-after:auto;break-after:auto}
*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}`;
    document.head.appendChild(st); document.body.appendChild(root);
    const imgs = Array.from(root.querySelectorAll('img'));
    const ready = Promise.all(imgs.map(im => (im.complete ? null : new Promise(r => { im.onload = r; im.onerror = r; }))));
    const cleanup = () => { setTimeout(() => { root.remove(); st.remove(); resolve(); }, 500); };
    ready.then(() => setTimeout(() => {
      window.addEventListener('afterprint', cleanup, { once: true });
      window.print();
      // Safari on iPad doesn't always fire afterprint
      setTimeout(() => { if (document.getElementById('njb-print-root')) cleanup(); }, 60000);
    }, 50));
  });
}
